/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import type { ComponentProps, ReactElement, ReactNode } from 'react'
import { forwardRef, useCallback, useSyncExternalStore } from 'react'
import { cn } from '@/lib/utils'
import { SideNavContext } from './side-nav-context'
import {
  readSideNavExpandedCookie,
  SIDE_NAV_COOKIE_NAME,
  subscribeSideNavCookie,
  writeSideNavExpandedCookie,
} from './side-nav-cookie'
import { useSideNavSsrCookies } from './side-nav-cookie-context'

export interface SideNavProps extends ComponentProps<'div'> {
  /** Which side of the screen the sidebar is on. Defaults to 'left'. */
  side?: 'left' | 'right'
  /** Initial expanded state when uncontrolled and no cookie is set. Defaults to true. */
  defaultExpanded?: boolean
  /**
   * Cookie used to remember expanded/collapsed state across page navigations.
   * Defaults to a shared name so all SideNavs stay in sync. Pass a different name to persist independently.
   */
  cookieName?: string
  /** Controlled expanded state. */
  expanded?: boolean
  /** Called when the expanded state changes. */
  onExpandedChange?: (expanded: boolean) => void
  /** When false, the sidebar cannot be collapsed and the toggle button is hidden. Defaults to true. */
  collapsible?: boolean
  /** When true, the sidebar is fixed to the viewport and does not scroll with the page content. */
  fixed?: boolean
  /** Where the toggle button is positioned along the sidebar edge. Defaults to 'center'. */
  togglePosition?: 'top' | 'center' | 'bottom'
  /** Content rendered at the top of the sidebar, above the scrollable items area. */
  header?: ReactNode
  /** Icon shown in the title bar when the sidebar is collapsed. The title bar keeps its height and background. */
  collapsedTitleIcon?: ReactElement
  /** Width of the sidebar when expanded. Defaults to '280px'. */
  expandedWidth?: string | number
  /** Width of the sidebar when collapsed to icons only. Defaults to '60px'. */
  collapsedWidth?: string | number
  /** Aria-label for the toggle button when sidebar is expanded. */
  collapseAriaLabel?: string
  /** Aria-label for the toggle button when sidebar is collapsed. */
  expandAriaLabel?: string
  children: ReactNode
}

const DEFAULT_EXPANDED_WIDTH = '280px'
const DEFAULT_COLLAPSED_WIDTH = '60px'

export const SideNav = forwardRef<HTMLDivElement, SideNavProps>(function SideNav(props, ref) {
  const {
    side = 'left',
    defaultExpanded = true,
    cookieName = SIDE_NAV_COOKIE_NAME,
    expanded: expandedProp,
    onExpandedChange,
    collapsible = true,
    fixed = false,
    togglePosition = 'center',
    header,
    collapsedTitleIcon,
    expandedWidth = DEFAULT_EXPANDED_WIDTH,
    collapsedWidth = DEFAULT_COLLAPSED_WIDTH,
    collapseAriaLabel = 'Collapse sidebar',
    expandAriaLabel = 'Expand sidebar',
    children,
    className,
    style,
    ...rest
  } = props

  const ssrCookies = useSideNavSsrCookies()
  const ssrExpanded = ssrCookies[cookieName]
  const subscribe = useCallback(
    (onStoreChange: () => void) => subscribeSideNavCookie(cookieName, onStoreChange),
    [cookieName]
  )
  const storedExpanded = useSyncExternalStore(
    subscribe,
    () => readSideNavExpandedCookie(cookieName) ?? defaultExpanded,
    () => ssrExpanded ?? defaultExpanded
  )
  const expanded = expandedProp ?? (collapsible ? storedExpanded : defaultExpanded)

  const setExpanded = (next: boolean) => {
    if (!collapsible) {
      return
    }
    writeSideNavExpandedCookie(cookieName, next)
    onExpandedChange?.(next)
  }

  const toggle = () => {
    setExpanded(!expanded)
  }

  const buttonTop = togglePosition === 'top' ? 'top-12' : togglePosition === 'bottom' ? undefined : 'top-1/2'
  const buttonBottom = togglePosition === 'bottom' ? 'bottom-12' : undefined
  const buttonTransform = togglePosition === 'center' ? '-translate-y-1/2' : undefined

  const ToggleIcon =
    side === 'left' ? (expanded ? ChevronLeftIcon : ChevronRightIcon) : expanded ? ChevronRightIcon : ChevronLeftIcon

  const widthValue = expanded ? expandedWidth : collapsedWidth
  const widthStyle =
    typeof widthValue === 'number' ? `${widthValue}px` : typeof widthValue === 'string' ? widthValue : undefined
  const expandedWidthStyle =
    typeof expandedWidth === 'number'
      ? `${expandedWidth}px`
      : typeof expandedWidth === 'string'
        ? expandedWidth
        : undefined
  const showCollapsedTitleIcon = !expanded && collapsedTitleIcon != null

  return (
    <SideNavContext.Provider value={{ expanded, side, setExpanded }}>
      <div
        className={cn(
          'relative shrink-0 overflow-visible transition-[width] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)]',
          fixed ? 'fixed bottom-0 z-40' : 'h-full',
          fixed && side === 'left' ? 'left-0' : null,
          fixed && side === 'right' ? 'right-0' : null,
          fixed ? 'top-[59px]' : null,
          className
        )}
        ref={ref}
        style={{
          ...style,
          width: widthStyle,
        }}
        {...rest}
      >
        <div
          className={cn(
            'flex h-full w-full flex-col overflow-hidden border-border/60 bg-muted',
            side === 'left' ? 'border-r' : 'border-l'
          )}
        >
          {header !== undefined ? (
            <div
              className={cn(
                'relative shrink-0 overflow-hidden transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)]',
                expanded ? (side === 'left' ? 'pr-2 pl-4' : 'pr-4 pl-2') : 'px-2'
              )}
            >
              <div
                className={cn(showCollapsedTitleIcon && 'invisible whitespace-nowrap')}
                style={showCollapsedTitleIcon ? { width: expandedWidthStyle } : undefined}
              >
                {header}
              </div>
              {showCollapsedTitleIcon ? (
                <div
                  aria-hidden
                  className="absolute inset-0 flex min-h-0 items-center justify-center overflow-hidden border-border border-b bg-card/60 text-muted-foreground [&_svg]:size-6"
                >
                  {collapsedTitleIcon}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="flex-1 overflow-y-auto overflow-x-hidden py-2">
            <div className={cn(expanded ? 'px-3' : 'px-1')}>{children}</div>
          </div>
        </div>

        {collapsible ? (
          <div
            className={cn(
              'absolute z-1',
              buttonTop,
              buttonBottom,
              buttonTransform,
              side === 'left' ? '-right-[15px]' : '-left-[15px]'
            )}
          >
            <button
              aria-label={expanded ? collapseAriaLabel : expandAriaLabel}
              className="flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-xs transition-colors hover:border-border hover:bg-muted hover:text-foreground"
              onClick={toggle}
              type="button"
            >
              <ToggleIcon className="size-5" />
            </button>
          </div>
        ) : null}
      </div>
    </SideNavContext.Provider>
  )
})

SideNav.displayName = 'SideNav'
